import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, delay, distinctUntilChanged, lastValueFrom, Observable} from 'rxjs';
import {
    ApiOrdersGetRequest,
    ApiOrdersGetResponse,
    ApiOrdersSetRequest,
    ApiOrdersSetResponse, ApiOrdersUpdateRequest, ApiOrdersUpdateResponse
} from '@iw-shop/api/orders';
import {HttpClient} from '@angular/common/http';
import {EnvironmentService} from '@iw-shop/core//services/environment.service';
import {ApiOrdersConfirmationsRequest, ApiOrdersConfirmationsResponse} from '@api/order-confirmations';
import {AppState} from '@store/state/app.state';
import {Store} from '@ngrx/store';
import {filter, take} from 'rxjs/operators';
import {resetNotifications, setNotification} from '@store/actions/global.actions';
import {
    getSelectedBillingAddress,
    getSelectedDeliveryAddress,
    getSelectedCustomer
} from '@store/selectors/user.selector';
import {UserService} from '@iw-shop/user/services/user.service';
import {ConfigService} from '@iw-shop/config/service/config.service';
import {PaypalService} from '@iw-shop/payment/services/paypal.service';
import {PaymentService} from "@iw-shop/payment/services/payment.service";
import {deleteShoppingCart} from "@store/actions/shopping-cart.action";
import {logout} from "@store/actions/authentication.action";
import {GoogleTagManagerService} from "@iw-shop/google-tag-manager/services/google-tag-manager.service";
import {ApiArticle, ApiArticlesGetRequestParams} from "@api/articles";
import {ArticleService} from "@iw-shop/article/services/article.service";
import {HTTPQueue} from "@iw-shop/core/classes/http-queue";
import {resetShoppingCart} from "@store/actions/storage.action";

@Injectable({
    providedIn: 'root'
})
export class OrdersService {

    private articles: ApiArticle[] = [];

    constructor(
        private http: HttpClient,
        private environmentService: EnvironmentService,
        private store: Store<AppState>,
        private userService: UserService,
        private configService: ConfigService,
        private paypalService: PaypalService,
        private httpQueue: HTTPQueue,
        private paymentService: PaymentService,
        private articleService: ArticleService,
        private googleTagManagerService: GoogleTagManagerService) {
    }

    getHTMLConfirmation(customerId: string, orderId: string, withoutPrices: boolean = false): Observable<ApiOrdersGetResponse> {
        let expectedData: Record<string, number> = {
            htmlConfirmationCustomer: 1
        };

        if (withoutPrices) {
            expectedData = {
                htmlConfirmationCustomerWithoutPrices: 1
            };
        }
        const body: ApiOrdersGetRequest = {
            id: 'getHTMLConfirmation', command: 'orders/get', params: {
                filters: [
                    {
                        fields: ['customerId'],
                        values: [customerId]
                    },
                    {
                        fields: ['id'],
                        values: [orderId]
                    }
                ],
                orders: {
                    expectedData: expectedData
                }
            }
        };
        return this.http.post<ApiOrdersGetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    getOrderParamsFromState(): ApiOrdersSetRequest['params']['data'] | null {
        let orderParams: ApiOrdersSetRequest['params']['data'] | null = null;
        console.log('order.service.getOrdersParamsFromState1');
        combineLatest([this.store.select(getSelectedCustomer), this.store.select(getSelectedBillingAddress), this.store.select(getSelectedDeliveryAddress), this.store.select(state => state.storage), this.store.select(state => state.shoppingCart)])
            .pipe(
                take(1)
            )
            .subscribe(
                ([customer, customerAddress, customerDeliveryAddress, storage, shoppingCart]) => {
                    if (!customer) {
                        this.store.dispatch(setNotification({
                            notification: 'getOrderParamsFromState customer required',
                            notificationType: 'error'
                        }));
                    }
                    if (!customerAddress) {
                        this.store.dispatch(setNotification({
                            notification: 'getOrderParamsFromState customerAddress required',
                            notificationType: 'error'
                        }));
                    }
                    if (!storage.shoppingCart?.id) {
                        this.store.dispatch(setNotification({
                            notification: 'getOrderParamsFromState shopping cart id required',
                            notificationType: 'error'
                        }));
                    }
                    if (!storage.shoppingCart?.token) {
                        this.store.dispatch(setNotification({
                            notification: 'getOrderParamsFromState shopping cart token required',
                            notificationType: 'error'
                        }));
                    }

                    if (customer &&
                        customerAddress &&
                        storage.shoppingCart?.id &&
                        storage.shoppingCart?.token) {
                        let address: ApiOrdersSetRequest['params']['data']['address'] = {
                            additionalAddressInformation: customerAddress?.additionalAddressInformation,
                            company: customerAddress?.company,
                            country: customerAddress?.country,
                            elevator: customerAddress?.elevator,
                            floor: customerAddress?.floor,
                            mobile: customerAddress?.mobile,
                            phone: customerAddress?.phone,
                            postcode: customerAddress?.postcode ?? '',
                            salutation: customerAddress?.salutation,
                            street: customerAddress?.street ?? '',
                            title: customerAddress?.title,
                            town: customerAddress?.town ?? ''
                        };

                        if (customerAddress?.gesoftName1) {
                            address.gesoftName1 = customerAddress.gesoftName1;
                        }
                        if (customerAddress?.gesoftName2) {
                            address.gesoftName2 = customerAddress.gesoftName2;
                        }
                        if (customerAddress?.firstName) {
                            address.firstName = customerAddress.firstName;
                        }
                        if (customerAddress?.name) {
                            address.name = customerAddress.name;
                        }

                        let deliveryAddress: ApiOrdersSetRequest['params']['data']['deliveryAddress'] | undefined = undefined;
                        if (customerDeliveryAddress &&
                            customerDeliveryAddress.addressId != customerAddress?.addressId) {
                            deliveryAddress = {
                                additionalAddressInformation: customerDeliveryAddress?.additionalAddressInformation,
                                company: customerDeliveryAddress?.company,
                                country: customerDeliveryAddress?.country,
                                elevator: customerDeliveryAddress?.elevator,
                                floor: customerDeliveryAddress?.floor,
                                mobile: customerDeliveryAddress?.mobile,
                                phone: customerDeliveryAddress?.phone,
                                postcode: customerDeliveryAddress?.postcode ?? '',
                                salutation: customerDeliveryAddress?.salutation,
                                street: customerDeliveryAddress?.street ?? '',
                                title: customerDeliveryAddress?.title,
                                town: customerDeliveryAddress?.town ?? ''
                            };
                            if (customerDeliveryAddress?.gesoftName1) {
                                deliveryAddress.gesoftName1 = customerDeliveryAddress.gesoftName1;
                            }
                            if (customerDeliveryAddress?.gesoftName2) {
                                deliveryAddress.gesoftName2 = customerDeliveryAddress.gesoftName2;
                            }
                            if (customerDeliveryAddress?.firstName) {
                                deliveryAddress.firstName = customerDeliveryAddress.firstName;
                            }
                            if (customerDeliveryAddress?.name) {
                                deliveryAddress.name = customerDeliveryAddress.name;
                            }
                        }
                        orderParams = {
                            shoppingCartId: storage.shoppingCart.id,
                            token: storage.shoppingCart.token,
                            email: this.userService.getUserEmail() || customer.email,
                            type: 'order',
                            status: 'progressStarted',
                            priceGroup: customer?.priceGroup || '1',
                            priceMode: customer?.priceMode || 'gross',
                            address
                        };

                        if (shoppingCart.deliveryDate) {
                            orderParams.deliveryDate = shoppingCart.deliveryDate;
                        }

                        if (shoppingCart.deliveryPeriod) {
                            orderParams.deliveryPeriod = shoppingCart.deliveryPeriod;
                        }
                        if (shoppingCart.note) {
                            orderParams.note = shoppingCart.note;
                        }

                        // Kein Gast
                        if (!customer?.guest) {
                            if (customer?.id) {
                                orderParams.customerId = customer.id;
                            }
                            if (customer?.customerNumber) {
                                orderParams.customerNumber = customer.customerNumber;
                            }
                            if (customer?.status) {
                                orderParams.customerStatus = customer.status;
                            }
                        }

                        if (deliveryAddress) {
                            orderParams.deliveryAddress = deliveryAddress;
                        }

                    } else {
                        console.log('order.service.getOrdersParamsFromState2', customer, customerAddress, storage.shoppingCart);
                    }
                });
        return orderParams;
    }

    getRecipients(type: 'order' | 'request'): { email: string, template: 'dealer' | 'customer' }[] {
        let recipients: { email: string, template: 'dealer' | 'customer' }[] = [];
        this.configService.getEmailAddressesForOrderConfirmation(type).map(
            email => {
                recipients.push({email, template: 'dealer'});
            }
        );
        this.store.select(getSelectedCustomer).pipe(take(1))
            .subscribe(
                r => {
                    if (r?.guest) {
                        if (r?.email) {
                            recipients.push({email: r.email, template: 'customer'});
                        }
                    } else {
                        this.store.select(state => state.user).pipe(take(1))
                            .subscribe(
                                r => {
                                    if (r?.email) {
                                        recipients.push({email: r.email, template: 'customer'});
                                    }
                                }
                            );
                    }

                }
            )


        return recipients;
    }

    getPaymentMethodValuesForOrder(paymentMethodName: 'stripe' | 'paypal' | 'klarna', externalPaymentId: string): ApiOrdersSetRequest['params']['data']['paymentMethod'] {
        let paymentMethod: ApiOrdersSetRequest['params']['data']['paymentMethod'] = {
            name: '',
            nameInternal: '',
            description: '',
            externalPaymentId: externalPaymentId
        };

        this.store.select(state => state.config).pipe(take(1)).subscribe(
            r => {
                if (paymentMethodName == 'paypal' &&
                    r.paymentMethods?.paypal) {
                    paymentMethod = {
                        name: r.paymentMethods.paypal.name,
                        nameInternal: r.paymentMethods.paypal.nameInternal,
                        description: r.paymentMethods.paypal.description,
                        externalPaymentId: externalPaymentId
                    };
                }
                if (paymentMethodName == 'stripe' &&
                    r.paymentMethods?.stripe) {
                    paymentMethod = {
                        name: r.paymentMethods.stripe.name,
                        nameInternal: r.paymentMethods.stripe.nameInternal,
                        description: r.paymentMethods.stripe.description,
                        externalPaymentId: externalPaymentId
                    };
                }
                if (paymentMethodName == 'klarna' &&
                    r.paymentMethods?.klarna) {
                    paymentMethod = {
                        name: r.paymentMethods.klarna.name,
                        nameInternal: r.paymentMethods.klarna.nameInternal,
                        description: r.paymentMethods.klarna.description,
                        externalPaymentId: externalPaymentId
                    };
                }
            }
        );
        return paymentMethod;
    }

    async pushOrderToGoogleTagManager() {
        let articleNumbers: string[] = [];
        this.store.select(state => state.shoppingCart)
            .pipe(take(1))
            .subscribe(
                r => {
                    if (r.id) {
                        articleNumbers = r.items?.articles.map(a => {
                            return a.articleNumber
                        });
                    }
                }
            )
        let articleRequestParams: ApiArticlesGetRequestParams = {
            filters: [
                {field: 'articleNumber', value: "^(" + articleNumbers.join('|') + ")$", useRegex: 1}
            ],
            articles: {
                limit: 0,
                expectedData: {
                    articleNumber: 1,
                    category: 1,
                    groups: 1,
                    brand: 1
                }
            }
        };
        if (this.configService.getCustomerId()) {
            articleRequestParams.customerId = this.configService.getCustomerId() ?? '';
        }

        let r = await lastValueFrom(this.articleService.get(
            articleRequestParams
        ));
        this.articles = [];
        if (r.result?.articles?.data) {
            this.articles = r.result.articles.data;
        }


        this.store.select(state => ({
            cart: state.shoppingCart,
            config: state.config,
            customer: getSelectedCustomer(state)
        }))
            .pipe(take(1)).subscribe(
            ({cart, config, customer}) => {
                if (cart.id) {
                    let total = 0;
                    let deliveryTotal = 0;
                    cart.items.dynamicArticles.filter(r => r.category == 'delivery' || r.category == 'deliverySelectable' || r.category == 'deliveryOverlay').map(r => {
                        if (customer?.priceMode == 'net') {
                            total = r.netSumTotal + total;
                            deliveryTotal = r.netSumTotal + deliveryTotal;
                        } else {
                            total = r.grossSumTotal + total;
                            deliveryTotal = r.grossSumTotal + deliveryTotal;
                        }
                    });
                    let items: any[] = [];
                    cart.items.articles.map(a => {
                        total = (a.grossPrice * a.amount) + total;
                        items.push({
                            item_id: a.articleNumber,
                            item_name: a.name,
                            affiliation: config.shopUrl ?? '',
                            discount: 0,
                            item_brand: this.articles.filter(x => x.articleNumber == a.articleNumber)[0].brand ?? '',
                            item_category: (this.articles.filter(x => x.articleNumber == a.articleNumber)[0].groups[0].groupName ?? '') + ' ' + (this.articles.filter(x => x.articleNumber == a.articleNumber)[0].groups[0].group1Name ?? ''),
                            price: a.grossPrice,
                            quantity: a.amount
                        })
                    });
                    cart.items.dynamicArticles.map(a => {
                        if (a.category != 'delivery' &&
                            a.category != 'deliveryOverlay' &&
                            a.category != 'deliverySelectable') {
                            total = (a.grossPrice * a.amount) + total;
                            items.push({
                                item_id: a.articleNumber,
                                item_name: a.name,
                                affiliation: config.shopUrl ?? '',
                                discount: 0,
                                item_category: 'dynamicArticle',
                                price: a.grossPrice,
                                quantity: a.amount
                            });
                        }
                    });
                    cart.items.vouchers.map(v => {
                        total = v.grossPrice + total;
                        items.push({
                            item_id: v.code,
                            item_name: 'Gutschein',
                            affiliation: config.shopUrl ?? '',
                            discount: 0,
                            item_category: 'dynamicArticle',
                            price: v.grossPrice,
                            quantity: 1
                        })
                    });
                    this.googleTagManagerService.pushTag({event: 'orderCompleted'});
                    this.googleTagManagerService.pushTag({event: 'purchase', ecommerce: null});
                    let tag = {
                        event: 'purchase',
                        ecommerce: {
                            transaction_id: cart.id,
                            // Sum of (price * quantity) for all items.
                            transactionTotal: total,
                            transactionShipping: deliveryTotal,
                            items: items,
                        }
                    };
                    this.googleTagManagerService.pushTag(tag);
                }

            }
        )
    }

    setOrder(jsonId: string, orderParams: ApiOrdersSetRequest['params']['data'], paymentProvider?: {
        name: 'paypal' | 'paypalExpress' | 'stripe' | 'stripeExpress' | 'klarna',
        externalPaymentId?: string
    }): Promise<boolean> {

        let requestCounter: number = 0;
        let requestSuccessCounter$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

        if (paymentProvider?.externalPaymentId) {
            orderParams.paymentMethod = {
                name: orderParams.paymentMethod?.name ?? paymentProvider.name,
                nameInternal: orderParams.paymentMethod?.nameInternal ?? paymentProvider.name,
                description: orderParams.paymentMethod?.description ?? '',
                externalPaymentId: paymentProvider.externalPaymentId
            }
        }

        return new Promise<boolean>(
            (resolve, reject) => {
                this.httpQueue.invoke(this.set(jsonId, orderParams)).subscribe(
                    {
                        next: async r => {
                            let orderId = r.result?.orders.data.id;
                            if (orderId) {
                                if (paymentProvider?.externalPaymentId) {
                                    if (orderParams.paymentMethod) {
                                        orderParams.paymentMethod.externalPaymentId = paymentProvider.externalPaymentId;
                                    }

                                    let shoppingCartId: string = '';
                                    this.store.select(state => state.shoppingCart.id).pipe(take(1)).subscribe(r => {
                                        shoppingCartId = r;
                                    })

                                    switch (paymentProvider.name) {
                                        case 'paypalExpress':
                                            requestCounter++;
                                            let r = await lastValueFrom(this.paypalService.setPayment(orderId, shoppingCartId, paymentProvider.externalPaymentId));
                                            if (!r.result?.payments.data.id) {
                                                reject('payments/set failed #paypal');
                                            }
                                            requestSuccessCounter$.next(requestSuccessCounter$.getValue() + 1);

                                            // set reference
                                            requestCounter++;
                                            this.paymentService.set(orderId ?? '', paymentProvider.name, paymentProvider.externalPaymentId ?? '').subscribe(
                                                {
                                                    next: () => {
                                                        requestSuccessCounter$.next(requestSuccessCounter$.getValue() + 1);
                                                    },
                                                    error: () => {
                                                        reject('payments/set failed #paypal');
                                                    }
                                                }
                                            )
                                            break;

                                        // Hat nur bei stripeExpress relevanz (Apple & Google Pay)
                                        case 'stripe':
                                        case 'stripeExpress':
                                            requestCounter++;
                                            this.paymentService.set(orderId ?? '', paymentProvider.name, paymentProvider.externalPaymentId ?? '').subscribe(
                                                {
                                                    next: () => {
                                                        requestSuccessCounter$.next(requestSuccessCounter$.getValue() + 1);
                                                    },
                                                    error: () => {
                                                        reject('payments/set failed #stripe');
                                                    }
                                                }
                                            )
                                            break;

                                        /*case 'klarna':
                                            requestCounter++;
                                            this.paymentService.set(orderId ?? '', paymentProvider.name, paymentProvider.externalPaymentId ?? '').subscribe(
                                                {
                                                    next: () => {
                                                        requestSuccessCounter$.next(requestSuccessCounter$.getValue() + 1);
                                                    },
                                                    error: () => {
                                                        reject('payments/set failed #klarna');
                                                    }
                                                }
                                            )
                                            break;*/
                                    }
                                }
                                this.getRecipients('order').map(
                                    recipient => {
                                        /*requestCounter++;*/
                                        this.sendEmailConfirmation(r.result?.orders.data.id || '', r.result?.orders.data.token || '', recipient.email, recipient.template).subscribe(
                                            {
                                                next: (r) => {
                                                    if (!r.result?.orderConfirmations) {
                                                        this.store.dispatch(setNotification({
                                                            notification: 'Die Bestellbestätigung an ' + recipient.email + ' wurde nicht versendet.',
                                                            notificationType: 'error'
                                                        }));
                                                    } else {
                                                        /*requestSuccessCounter$.next(requestSuccessCounter$.getValue() + 1);*/
                                                    }
                                                },
                                                error: () => {
                                                    /*reject();*/
                                                }
                                            }
                                        )
                                    });

                                requestSuccessCounter$.subscribe(
                                    r => {
                                        if (r == requestCounter) {
                                            setTimeout(() => {
                                                requestSuccessCounter$.complete();
                                            }, 200);
                                            this.deleteShoppingCartAndLogoutIfActivated();
                                            resolve(true);
                                        }
                                    }
                                );

                                // GoogleTagManager active
                                if (this.googleTagManagerService.isAvailable()) {
                                    await this.pushOrderToGoogleTagManager();
                                }

                            } else {
                                reject('order id requrired');
                            }
                        },
                        error: () => {
                            reject('order/set failed');
                        }
                    }
                )
            }
        )
    }

    deleteShoppingCartAndLogoutIfActivated() {

        this.store.select(state => ({storage: state.storage, config: state.config}))
            .pipe(
                take(1)
            )
            .subscribe(r => {
                if (r.storage.shoppingCart?.id &&
                    r.storage.shoppingCart.token) {
                    this.store.dispatch(deleteShoppingCart({
                        id: r.storage.shoppingCart.id,
                        token: r.storage.shoppingCart.token
                    }));
                }
                if (r.config.logoutAfterOrder) {
                    setTimeout(() => {
                        this.store.dispatch(setNotification({
                            notification: 'Automatische Abmeldung erfolgt...',
                            notificationType: 'info'
                        }));
                    }, 1000)

                    this.store.select(state => state.shoppingCart).pipe(
                        filter(state => state.requestsCounter == state.requestsDone),
                        take(1),
                        delay(3000)
                    ).subscribe(
                        r => {
                            this.store.dispatch(resetNotifications({types: ['info']}));
                            this.store.dispatch(logout());
                        }
                    );
                }
            });
    }

    get(params: ApiOrdersGetRequest['params']): Observable<ApiOrdersGetResponse> {
        const body
            :
            ApiOrdersGetRequest = {id: 'getOrders', command: 'orders/get', params};
        return this.http.post<ApiOrdersGetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    set(jsonId: string, params: ApiOrdersSetRequest['params']['data']): Observable<ApiOrdersSetResponse> {
        const body: ApiOrdersSetRequest = {id: jsonId, command: 'orders/set', params: {data: params}};
        return this.http.post<ApiOrdersSetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    update(jsonId: string, params: ApiOrdersUpdateRequest['params']['data']): Observable<ApiOrdersUpdateResponse> {
        const body: ApiOrdersUpdateRequest = {id: jsonId, command: 'orders/update', params: {data: params}};
        return this.http.post<ApiOrdersUpdateResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    sendEmailConfirmation(orderId: string, token: string, email: string, template: 'dealer' | 'customer'): Observable<ApiOrdersConfirmationsResponse> {
        let customerId = '';
        this.store.select(getSelectedCustomer).pipe(take(1)).subscribe(
            r => {
                if (r?.id) {
                    customerId = r.id;
                }
            }
        )
        const body: ApiOrdersConfirmationsRequest = {
            id: 'sendConfirmation',
            command: 'orderConfirmations/set',
            params: {
                data: {
                    id: orderId,
                    token,
                    email,
                    template,
                    withoutPrices: !this.userService.hasLoggedInUserAccess({type: 'prices', customerId})
                }
            }
        };
        return this.http.post<ApiOrdersConfirmationsResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }
}
